export default function RecruitingToolkit() {
  return (
    <div className="max-w-3xl space-y-8">
      <h1 className="text-2xl font-bold text-white">D1 Soccer Recruiting Toolkit</h1>
      <p className="text-slate-400">
        Resources for student-athletes and families navigating the NCAA Division I soccer recruiting process.
      </p>

      <section>
        <h2 className="text-lg font-semibold mb-3 text-slate-100">Key Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ResourceLink
            title="NCAA Eligibility Center"
            description="Register and verify your academic eligibility"
            url="https://web3.ncaa.org/ecwr3/"
          />
          <ResourceLink
            title="NCAA D1 Soccer Rules"
            description="Recruiting rules, contact periods, and dead periods"
            url="https://www.ncaa.org/sports/soccer"
          />
          <ResourceLink
            title="Scholarship Limits"
            description="Men: 9.9 equivalency | Women: 14 head-count"
            url="https://www.ncaa.org/sports/soccer"
          />
          <ResourceLink
            title="Transfer Portal"
            description="NCAA transfer portal information"
            url="https://www.ncaa.org/sports/transfer-portal"
          />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3 text-slate-100">Glossary</h2>
        <dl className="space-y-2">
          <Term term="RPI" definition="Rating Percentage Index. Combines a team's winning percentage, opponents' winning percentage, and opponents' opponents' winning percentage." />
          <Term term="At-Large Bid" definition="Tournament spots awarded by committee to teams that didn't win their conference tournament." />
          <Term term="NLI" definition="National Letter of Intent. A binding agreement between a student-athlete and school." />
          <Term term="Equivalency Sport" definition="Men's soccer is an equivalency sport, meaning scholarships can be divided among players." />
          <Term term="Head-Count Sport" definition="Women's soccer is a head-count sport, meaning each scholarship covers a full ride." />
        </dl>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3 text-slate-100">Recruiting Timeline</h2>
        <Timeline />
      </section>
    </div>
  )
}

function ResourceLink({ title, description, url }: { title: string; description: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="card p-4 hover:border-accent/30 transition-colors block"
    >
      <h3 className="font-medium text-accent">{title}</h3>
      <p className="text-sm text-slate-400 mt-1">{description}</p>
    </a>
  )
}

function Term({ term, definition }: { term: string; definition: string }) {
  return (
    <div className="card px-4 py-3">
      <dt className="font-medium text-sm text-slate-100">{term}</dt>
      <dd className="text-sm text-slate-400 mt-1">{definition}</dd>
    </div>
  )
}

function Timeline() {
  const steps = [
    { year: 'Freshman', items: ['Start NCAA Eligibility Center registration', 'Research core course requirements', 'Begin building highlight reel'] },
    { year: 'Sophomore', items: ['Attend college ID camps and showcases', 'Start communicating with coaches', 'Track your core course progress'] },
    { year: 'Junior', items: ['Take SAT/ACT', 'Narrow your school list', 'Schedule unofficial visits', 'Send updated highlight video to coaches'] },
    { year: 'Senior', items: ['Complete Eligibility Center certification', 'Take official visits (max 5)', 'Submit applications', 'Sign NLI during signing period'] },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {steps.map((step) => (
        <div key={step.year} className="card-elevated p-4">
          <h3 className="font-semibold text-accent mb-2">{step.year}</h3>
          <ul className="space-y-1">
            {step.items.map((item, i) => (
              <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                <span className="text-accent/60 mt-0.5">-</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
